import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { isUserAllowedToHandleIndividualAchievementRequests } from 'shared-helpers/achievements';
import { Box } from 'components/common/box';
import { InfoCountTile } from 'components/common/info';
import { MyTeamNotificationsItem } from '.';

const MyTeamNotifications = () => {
  const {
    messages,
    pageState: { supervisor },
    serverConfig,
    user
  } = useReduxState();

  const { data } = useApiData({
    url: `/admin/supervisors/${user?.id}/supervisees/summary`
  });

  const canManageIndividualAchievements =
    serverConfig.individualAchievementsEnabled &&
    serverConfig.individualAchievementsUserRequireApproval &&
    isUserAllowedToHandleIndividualAchievementRequests({
      supervisorAllowed:
        serverConfig.individualAchievementsUserRequireApprovalOptions
          ?.supervisor?.enabled,
      supervisorConditions:
        serverConfig.individualAchievementsUserRequireApprovalOptions
          ?.supervisor?.conditions,
      user
    });

  const achievementsVisible = !serverConfig.hideAchievementsFromUI;
  const coursesVisible = !serverConfig.hideCoursesFromUI;
  const pathsVisible = !serverConfig.hidePathsFromUI;

  return (
    <div className="notifications">
      {achievementsVisible && (
        <>
          {supervisor.numberOfAssignedAchievements ? (
            <div className="notification">
              <InfoCountTile
                href="/my-team/follow/achievements"
                icon="check-circle"
                text={messages.myTeamAssignedAchievements}
                value={supervisor.numberOfAssignedAchievements}
              />
            </div>
          ) : null}

          {supervisor.numberOfAssignedAchievements ||
          canManageIndividualAchievements ? (
            <div className="notification">
              <Box padding={0} title={messages.commonPendingRequests}>
                <div className="items">
                  {supervisor.numberOfAssignedAchievements ? (
                    <MyTeamNotificationsItem
                      category="danger"
                      href="/my-team/follow/pending-requests"
                      icon="check-circle"
                      label={messages.commonAchievementsAlt}
                      value={data?.numberOfPendingAchievementRequests}
                    />
                  ) : null}

                  {canManageIndividualAchievements ? (
                    <MyTeamNotificationsItem
                      category="danger"
                      href="/my-team/follow/pending-requests"
                      icon="star"
                      label={messages.individualAchievements}
                      value={data?.numberOfPendingIndividualAchievementRequests}
                    />
                  ) : null}
                </div>
              </Box>
            </div>
          ) : null}
        </>
      )}

      {(coursesVisible || achievementsVisible) && (
        <div className="notification">
          <Box padding={0} title={messages.myTeamExpirations}>
            <div className="items">
              <MyTeamNotificationsItem
                category="warning"
                href="/my-team/review/expiring-completions"
                icon={['far', 'calendar-alt']}
                label={messages.myTeamReviewExpiring}
                value={data?.numberOfExpiringCompletions}
              />

              <MyTeamNotificationsItem
                category="danger"
                href="/my-team/review/expired-completions"
                icon={['far', 'calendar-times']}
                label={messages.myTeamReviewExpired}
                value={data?.numberOfExpiredCompletions}
              />
            </div>
          </Box>
        </div>
      )}

      {(coursesVisible || pathsVisible) && (
        <div className="notification">
          <Box padding={0} title={messages.commonTargetSchedules}>
            <div className="items">
              <MyTeamNotificationsItem
                category="warning"
                href="/my-team/review/upcoming-target-schedules"
                icon={['far', 'clock']}
                label={messages.commonUpcomingTargetSchedules}
                value={data?.numberOfUpcomingDeadlines}
              />

              <MyTeamNotificationsItem
                category="danger"
                href="/my-team/review/overdue-target-schedules"
                icon={['far', 'times-circle']}
                label={messages.commonOverdueTargetSchedules}
                value={data?.numberOfOverdueDeadlines}
              />
            </div>
          </Box>
        </div>
      )}

      <style jsx>{`
        .notifications {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 30px;
        }
        .notification {
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default MyTeamNotifications;
